import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, Typography, Container } from '@mui/material';
import { createStripeAccount, getUser, submitUserData } from '../../../apiCalls';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import { getAuth } from 'firebase/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import logger from 'src/logger';
function PersonalDetailsForm({ initialName = '', initialEmail = '', id = '', isNewUser = false }) {
    const { user, setUser } = useUserContext();
    const [isLoading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: initialName,
        email: initialEmail,
        displayName: '',
        phoneNumber: '',
        profilePicture: '',
        bio: '',
        location: '',
        id: id
    });
    const auth = getAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user && !initialName) {
            fetchUserProfile(user.uid);
        }
        setLoading(false);
    }, [user, initialName, initialEmail, id]);

    const fetchUserProfile = async (userId) => {
        try {
            const userDetails = await getUser(user.accessToken, userId);
            if (userDetails) {
                setFormData({
                    name: userDetails.name || '',
                    email: userDetails.email || '',
                    displayName: userDetails.displayName || '',
                    phoneNumber: userDetails.phoneNumber || '',
                    profilePicture: userDetails.profilePicture || '',
                    bio: userDetails.bio || '',
                    location: userDetails.location || '',
                    id: userId
                });
            } else {
                // Handle case where user doesn't exist yet
                setFormData({
                    name: initialName || '',
                    email: initialEmail || '',
                    displayName: '',
                    phoneNumber: '',
                    profilePicture: '',
                    bio: '',
                    location: '',
                    id: userId
                });
            }
        } catch (error) {
            logger.error('Error fetching user profile:', error);
            toast.error('Failed to fetch user profile.');
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        // Validate mandatory fields
        if (!formData.displayName || !formData.name || !formData.email) {
            toast.error('Please fill out all required fields.');
            return;
        }
        
        try {
            logger.info('using access token', user.accessToken);
            await submitUserData(user.accessToken, user.uid, formData);
            toast.success('User data updated successfully!');
            
            // Delay navigation to allow the success message to be displayed
            setTimeout(() => {
                navigate('/'); // Redirect to the home page after the delay
            }, 2000); // 2-second delay
        } catch (error) {
            toast.error('Failed to submit user data.');
            logger.error('Submission error:', error);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <ToastContainer position="bottom-center" />
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {isNewUser && (
                    <Typography color="error" variant="subtitle2" sx={{ mt: 1, mb: 2, fontWeight: 'bold' }}>
                        Please complete this form to finish your sign-up process.
                    </Typography>
                )}
                <Typography component="h1" variant="h5">
                    Personal Details Form
                </Typography>

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        id="displayName"
                        label="Display Name"
                        name="displayName"
                        autoComplete="displayName"
                        autoFocus
                        helperText="This name will be visible alongside images of your catch reports."
                        value={formData.displayName}
                        onChange={handleChange}
                    />
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        id="name"
                        label="Name"
                        name="name"
                        autoComplete="name"
                        value={formData.name}
                        onChange={handleChange}
                    />
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        autoComplete="email"
                        value={formData.email}
                        InputProps={{ readOnly: true }} // Make the field uneditable
                    />
                    <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="phoneNumber"
                        label="Phone Number"
                        name="phoneNumber"
                        autoComplete="tel"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                    />
                    <TextField
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        id="location"
                        label="Location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                    />
                    <input type="hidden" name="userId" value={formData.id} />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                    >
                        Submit
                    </Button>
                </Box>
            </Box>
        </Container>
    );
}

export default PersonalDetailsForm;
